import { Text, Title2, WasabiButton } from '@kk/ui';
import { useMemo, type HTMLAttributes, type KeyboardEvent } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { ColumnHead, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { useErrorMessage, useAssignments } from '@/data/hooks';
import { isoDateLabel, toIsoDate } from '@/lib/time';
import type { ShellContext } from '@/shell/Shell';
import { portalColors } from '@/theme/portalTokens';
import type { Assignment } from '@/types/wire';

/**
 * Where an assignment stands. "Draft" is O06's mission-list pill word, the same meaning here (saved, not pushed to
 * children). A published one is Upcoming, Running or Ended by its dates, in the family's calendar days (the 23 Oct
 * audit, T2-4 and T3-12): "Published" stayed on Reading Week weeks after it ended.
 */
export function assignmentState(
  a: Pick<Assignment, 'published' | 'window'>,
  today: string = toIsoDate(new Date()),
): 'draft' | 'upcoming' | 'running' | 'ended' {
  if (!a.published) return 'draft';
  if (today < a.window.start) return 'upcoming';
  if (today > a.window.end) return 'ended';
  return 'running';
}

function StatePill({ assignment }: { assignment: Assignment }) {
  const state = assignmentState(assignment);
  return (
    <span className={`status-pill ${state}`} data-testid={`state-${assignment.id}`}>
      {state === 'draft' ? (
        <Text variant="labelStrong" style={{ color: portalColors.amber }}>
          {deck.o06.draft}
        </Text>
      ) : (
        <Text variant="labelStrong" tone={state === 'ended' ? 'muted' : 'link'}>
          {placeholders.assignments[state]}
        </Text>
      )}
    </span>
  );
}

/** "Mon 21 Sep – Fri 25 Sep" — the two O03 WHEN values joined by the deck's en dash. */
function windowLabel(a: Pick<Assignment, 'window'>): string {
  return deck.o03.window(isoDateLabel(a.window.start), isoDateLabel(a.window.end));
}

/** O03 list: drafts and published assignments this staff member covers, newest first (the API's order). */
export function AssignmentsScreen() {
  const errorMessage = useErrorMessage();
  const { isPhone } = useOutletContext<ShellContext>();
  const navigate = useNavigate();
  const assignments = useAssignments();
  const rows = useMemo(() => assignments.data ?? [], [assignments.data]);

  const status = assignments.isPending
    ? placeholders.empty.loading
    : assignments.error
      ? errorMessage(assignments.error)
      : rows.length === 0
        ? placeholders.assignments.empty
        : null;

  const open = (a: Assignment) => navigate(`/assignments/${encodeURIComponent(a.id)}`);

  /**
   * The whole row opens the builder on both layouts (the desktop row is ~48px tall, the phone card taller).
   * role="link" navigates, so Enter follows it; Space is left to the page, as it is on a real link.
   */
  const rowProps = (a: Assignment): HTMLAttributes<HTMLDivElement> & { 'data-testid': string } => ({
    role: 'link',
    tabIndex: 0,
    onClick: () => open(a),
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        open(a);
      }
    },
    'data-testid': `row-${a.id}`,
  });

  // The deck names this section only in the left rail; there is no drawn title, count template or subtitle
  // for the list (OPEN_QUESTIONS 18l), so the header is the rail word and the CTA.
  const header = (
    <div className="page-header">
      <div className="page-header-text">
        <Title2 tone="ink">{deck.nav.assignments}</Title2>
      </div>
      {isPhone ? null : (
        <div className="actions">
          <WasabiButton
            label={placeholders.assignments.newAssignment}
            size="sm"
            fullWidth={false}
            onPress={() => navigate('/assignments/new')}
            testID="new-assignment"
          />
        </div>
      )}
    </div>
  );

  if (isPhone)
    return (
      <>
        {/* Assignments is not in the phone segments bar (it is reached from the ≡ menu), so the header stays. */}
        {header}
        {status ? <Status>{status}</Status> : null}
        {rows.map((a) => (
          <div className="card-row clickable" key={a.id} {...rowProps(a)}>
            <div className="cell grow">
              <Text variant="rowTitle" tone="ink" numberOfLines={2}>
                {a.title}
              </Text>
              <Text variant="meta" tone="muted" numberOfLines={1}>
                {windowLabel(a)}
              </Text>
              {/* The deck draws "TASKS · 3" as a section label, so it keeps its own line, not the meta run. */}
              <Text variant="time" tone="muted" numberOfLines={1}>
                {deck.o03.tasks(a.tasks.length)}
              </Text>
            </div>
            <div className="cell">
              <StatePill assignment={a} />
              <Text variant="meta" tone="slate">
                {deck.o05.childCount(a.childCount)}
              </Text>
            </div>
          </div>
        ))}
        <div className="sticky-footer">
          <WasabiButton
            label={placeholders.assignments.newAssignment}
            onPress={() => navigate('/assignments/new')}
            testID="new-assignment"
          />
        </div>
      </>
    );

  return (
    <>
      {header}
      <div className="table-card">
        <div className="table-scroll">
          <div className="grid-head grid-assignments">
            <ColumnHead>{placeholders.assignments.columns.assignment}</ColumnHead>
            <ColumnHead>{placeholders.assignments.columns.tasks}</ColumnHead>
            <ColumnHead>{placeholders.assignments.columns.children}</ColumnHead>
            <ColumnHead>{placeholders.assignments.columns.status}</ColumnHead>
          </div>
          {rows.map((a) => (
            <div className="grid-row grid-assignments clickable" key={a.id} {...rowProps(a)}>
              <div className="cell">
                <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                  {a.title}
                </Text>
                <Text variant="meta" tone="muted" numberOfLines={1}>
                  {windowLabel(a)}
                </Text>
              </div>
              {/*
               * The heads carry the words, as O03's ASSIGN TO card draws them; the row is one role="link",
               * so each bare count repeats its head in the accessible name.
               */}
              <Text
                variant="bodySm"
                tone="slate"
                accessibilityLabel={`${placeholders.assignments.columns.tasks} ${a.tasks.length}`}
              >
                {String(a.tasks.length)}
              </Text>
              <Text
                variant="bodySm"
                tone="slate"
                accessibilityLabel={`${placeholders.assignments.columns.children} ${a.childCount}`}
              >
                {String(a.childCount)}
              </Text>
              <StatePill assignment={a} />
            </div>
          ))}
        </div>
        {/* Loading / error / empty sit in the card and not in the scroller, as O01 does. */}
        {status ? (
          <div className="grid-row grid-assignments">
            <Status>{status}</Status>
          </div>
        ) : null}
      </div>
    </>
  );
}
